import { useCallback } from 'react'
import HttpStatus from 'http-status-codes'

import { config } from '../../components/Init/Init'
import { showStore } from '../../sweepstakes/messages'
import { sweepsGeoStatusIsBlocked, verifyNotFrozen } from '../../services/utils'
import { useSetPlayerSmsInfo } from '../../services/store/playerStore/playerActions'
import { useStateValue } from '../../services/state/StateProvider'
import { usePlayerStore } from '../../services/store'
import { useCryptoPackageModal } from '../../components/CryptoPackageModal'
import { useModals } from '../../services/providers/ModalsProvider'
import { useGetPlayerPurchaseLimitData } from '../useGetPlayerPurchaseLimitData'
import { purchaseBlockedModalState } from '../../components/Modals/PurchaseBlockedModal/constants'
import { useSetCasinoPurchaseInfoPending } from '../../services/store/casinoStore/casinoActions'

export const useOpenCoinStore = () => {
  const [_, dispatch] = useStateValue()

  const playerStore = usePlayerStore()
  const kycStatus = playerStore.use.kycStatus()
  const playerGeolocationStatus = playerStore.use.geolocationStatus()
  const playerSocialData = playerStore.use.socialData()
  const playerFreezeInfo = playerStore.use.playerFreezeInfo()

  const { setPlayerSmsInfo } = useSetPlayerSmsInfo()
  const { setCasinoPurchaseInfoPending } = useSetCasinoPurchaseInfoPending()
  const { handleOpenCryptoPackageModal } = useCryptoPackageModal()
  const { getPlayerPurchaseLimitData } = useGetPlayerPurchaseLimitData()
  const { openPurchaseBlockedModal } = useModals()

  const fetchPlayerPurchaseLimitData = useCallback(async () => {
    setCasinoPurchaseInfoPending(true)

    try {
      const response = await getPlayerPurchaseLimitData()

      if (response.status === HttpStatus.NO_CONTENT) {
        return null
      }

      return response.json().then(data => data)
    } catch (error) {
      openPurchaseBlockedModal({ state: purchaseBlockedModalState.unable })
      console.log('Coin store error: ', error)
    } finally {
      setCasinoPurchaseInfoPending(false)
    }
  }, [getPlayerPurchaseLimitData])

  const openPurchaseLimitKyc = useCallback(() => {
    const kycPopupReason =
      kycStatus === config.kycStatuses.Verified
        ? config.purchaseLimitKycStatus.PostKyc
        : config.purchaseLimitKycStatus.PreKyc

    dispatch({
      type: 'setModal',
      modals: {
        purchaseLimitKyc: { display: true, data: { reason: kycPopupReason } }
      }
    })
  }, [dispatch, kycStatus])

  const openDepositFunction = config.enableCryptoWallet ? handleOpenCryptoPackageModal : showStore

  const openCoinStore = useCallback(async () => {
    // keep checks order

    // geolocation block check
    if (sweepsGeoStatusIsBlocked(playerGeolocationStatus, playerSocialData)) {
      return
    }

    // coin store disabled in CST
    if (config.enableCryptoWallet) {
      const sweepsStoreBlocked =
        playerSocialData?.features &&
        playerSocialData?.features.indexOf(config.classicAccountFeatures.sweepsStoreBlocked) > -1

      if (sweepsStoreBlocked) {
        return openPurchaseBlockedModal({ state: purchaseBlockedModalState.unable })
      }
    }

    // deposit not frozen
    if (verifyNotFrozen(playerFreezeInfo, config.freezeEvents.depositFrozen)) {
      setPlayerSmsInfo({ smsVerifiedFunc: openDepositFunction })
      return
    }

    // deposit frozen
    if (config.enableCryptoWallet) {
      const playerPurchaseLimitData = await fetchPlayerPurchaseLimitData()

      if (playerPurchaseLimitData) {
        const { spentInActivePeriod, activeLimit } = playerPurchaseLimitData

        if (spentInActivePeriod > activeLimit.amount) {
          openPurchaseBlockedModal({ period: activeLimit.periodHours, state: purchaseBlockedModalState.limit })
        } else {
          openPurchaseBlockedModal({ state: purchaseBlockedModalState.unable })
        }
      } else {
        openPurchaseBlockedModal({ state: purchaseBlockedModalState.unable })
      }
    } else {
      openPurchaseLimitKyc()
    }
  }, [
    playerGeolocationStatus,
    playerSocialData,
    playerFreezeInfo,
    fetchPlayerPurchaseLimitData,
    setPlayerSmsInfo,
    openDepositFunction,
    openPurchaseBlockedModal,
    openPurchaseLimitKyc
  ])

  return { openCoinStore }
}
